/**
 * Cross-tab arbitration for the call module's ring sounds (D7 + D8).
 *
 * Design decision 7 makes ringing a property of the *user*, not of a tab: one
 * ring sound at a time, no matter how many tabs are open. Two rules follow, and
 * both are served by this one channel:
 *
 *   D7 — a ringback playing anywhere suppresses the ringtone everywhere. You
 *        placed a call; someone else calls you; the banner appears but stays
 *        silent so the two tones never overlap.
 *   D8 — when several tabs would ring for the same invite, exactly one wins.
 *
 * ## Why claims are *challenged* rather than counted
 *
 * The obvious election — "collect every claim, lowest id rings" — has a failure
 * mode that is silent and permanent: a tab that dies without running its
 * teardown (crash, OOM kill, forced quit) leaves a claim nobody can retract, and
 * the surviving tabs defer to a ghost forever. Heartbeats would fix it at the
 * cost of a timer and a TTL that has to be tuned.
 *
 * Instead every rule here is expressed as a *challenge that only a live tab can
 * make*. A claiming tab starts ringing immediately, and stops only if some other
 * tab answers back with a better claim. Dead tabs answer nothing, so they lose
 * automatically; there is no timer anywhere in this file, and no state that has
 * to expire.
 *
 * `BroadcastChannel` does not deliver a message to the context that posted it,
 * so a tab never hears its own claims and the exchange terminates: the higher id
 * answers a claim once, the lower id yields and answers nothing.
 *
 * ## Ordering
 *
 * Tab ids are time-ordered (`Date.now()` prefix), so "lowest id wins" resolves
 * to "the oldest tab rings". That matters for a late-opened tab: a random id
 * would have a coin-flip chance of outranking the tab already ringing and
 * restarting the tone somewhere else mid-invite. An older-wins ordering can
 * never be stolen by a tab that arrives later.
 *
 * ## Degradation
 *
 * Without `BroadcastChannel` (older browsers, or jsdom) every tab is an island:
 * local ringback suppression — the case AC14 actually tests — still works, and
 * multi-tab behaviour falls back to today's "every tab rings". That is the
 * pre-existing behaviour, not a regression.
 */

const CHANNEL_NAME = "weco-call-ring";

type Message =
  /** "I intend to ring for this invite." Doubles as the challenge others answer. */
  | { t: "claim"; tab: string; invite: string }
  /** "I have stopped wanting to ring" — lets whoever yielded to me take over. */
  | { t: "release"; tab: string; invite: string }
  /** "A ringback is / is no longer playing here." */
  | { t: "ringback"; tab: string; active: boolean }
  /** Sent on pagehide so the others drop everything this tab was holding. */
  | { t: "bye"; tab: string };

/**
 * Time-ordered so that comparing two ids compares tab age. The random suffix
 * breaks ties between tabs opened inside the same millisecond.
 */
function makeTabId(): string {
  const stamp = Date.now().toString(36).padStart(9, "0");
  const rand = Math.random().toString(36).slice(2, 10);
  return `${stamp}-${rand}`;
}

let tabId = makeTabId();
let channel: BroadcastChannel | null = null;
let installed = false;

const listeners = new Set<() => void>();

/** A ringback is playing in *this* tab. Known synchronously — no round-trip. */
let localRingback = false;
/** Tabs that have told us a ringback is playing there. */
const remoteRingback = new Set<string>();
/** The invite this tab currently wants to ring for, if any. */
let myInvite: string | null = null;
/** Live tabs that answered our claim with an older id. Non-empty ⇒ we stay quiet. */
const outrankedBy = new Set<string>();

function notify(): void {
  for (const listener of listeners) listener();
}

function post(message: Message): void {
  channel?.postMessage(message);
}

function handle(message: Message): void {
  switch (message.t) {
    case "claim": {
      // Answer with our ringback regardless of which invite they are claiming:
      // D7 suppresses the ringtone for *any* invite while we ring out.
      if (localRingback) post({ t: "ringback", tab: tabId, active: true });

      if (myInvite !== message.invite) return;
      if (message.tab < tabId) {
        // They are older. Stand down until they release or go away.
        if (!outrankedBy.has(message.tab)) {
          outrankedBy.add(message.tab);
          notify();
        }
      } else {
        // We are older and they cannot hear their own claim, so tell them we
        // exist. They will yield and will not answer this — the exchange ends.
        post({ t: "claim", tab: tabId, invite: myInvite });
      }
      return;
    }
    case "release": {
      if (outrankedBy.delete(message.tab)) notify();
      return;
    }
    case "ringback": {
      const had = remoteRingback.has(message.tab);
      if (message.active) remoteRingback.add(message.tab);
      else remoteRingback.delete(message.tab);
      if (had !== message.active) notify();
      return;
    }
    case "bye": {
      // Both deletes must run — `||` would skip the second one.
      const droppedRingback = remoteRingback.delete(message.tab);
      const droppedClaim = outrankedBy.delete(message.tab);
      if (droppedRingback || droppedClaim) notify();
      return;
    }
  }
}

function install(): void {
  if (installed) return;
  installed = true;

  if (typeof BroadcastChannel === "undefined") return;

  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<Message>) => handle(event.data);

  if (typeof window !== "undefined") {
    // `pagehide` fires on close, navigation and bfcache eviction alike, which
    // `beforeunload` does not. A missed `bye` is survivable by design — see the
    // challenge model above — so this is an optimisation, not a correctness
    // requirement.
    window.addEventListener("pagehide", () => post({ t: "bye", tab: tabId }));
  }
}

/** Whether a ringtone may sound in this tab right now (D7 + D8 combined). */
export function isRingtoneAllowed(): boolean {
  return !localRingback && remoteRingback.size === 0 && outrankedBy.size === 0;
}

/** Subscribe to changes in {@link isRingtoneAllowed}. Returns an unsubscribe. */
export function subscribeRingState(listener: () => void): () => void {
  install();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Announce that this tab is (or has stopped) playing a ringback — D7. */
export function setLocalRingback(active: boolean): void {
  install();
  if (localRingback === active) return;
  localRingback = active;
  post({ t: "ringback", tab: tabId, active });
  notify();
}

/**
 * Declare that this tab wants to ring for `invite` — D8.
 *
 * Ringing starts optimistically; {@link isRingtoneAllowed} only turns false if a
 * live older tab answers. Claiming a different invite resets the contest, since
 * whoever outranked us for the previous one has no say over this one.
 */
export function claimRingtone(invite: string): void {
  install();
  if (myInvite === invite) return;
  if (myInvite) post({ t: "release", tab: tabId, invite: myInvite });
  myInvite = invite;
  outrankedBy.clear();
  post({ t: "claim", tab: tabId, invite });
  notify();
}

/** Withdraw this tab's claim, freeing any tab that yielded to it. */
export function releaseRingtone(): void {
  if (!myInvite) return;
  post({ t: "release", tab: tabId, invite: myInvite });
  myInvite = null;
  outrankedBy.clear();
  notify();
}

/** This tab's election id. Exposed for tests and for diagnostic logging. */
export function getTabId(): string {
  return tabId;
}

/**
 * Test seam: drop all state and rebuild the channel, optionally under a fixed
 * tab id so a test can control who wins the election.
 */
export function __resetRingCoordinator(id?: string): void {
  channel?.close();
  channel = null;
  installed = false;
  listeners.clear();
  localRingback = false;
  remoteRingback.clear();
  myInvite = null;
  outrankedBy.clear();
  tabId = id ?? makeTabId();
}
